import type { ChangeEventHandler, DragEventHandler, ReactNode } from "react";

type FileUploadDropzoneProps = {
  accept: string;
  dragActive: boolean;
  emptyStateLabel: ReactNode;
  helperText: ReactNode;
  inputId: string;
  isUploading: boolean;
  multiple?: boolean;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onDrag: DragEventHandler<HTMLDivElement>;
  onDrop: DragEventHandler<HTMLDivElement>;
  onReset: () => void;
  resetLabel: string;
  selectedFile?: File | null;
  selectedFiles?: File[];
  selectedMode?: "single" | "list" | "count-list";
  tone?: "blue" | "purple";
};

const toneClasses = {
  blue: {
    active: "border-blue-500 bg-blue-50 dark:bg-[#5B5353]/30",
    button: "text-blue-600 hover:text-blue-700 dark:text-[#FFF1F3] dark:hover:text-[#FFF8F9]",
  },
  purple: {
    active: "border-purple-500 bg-purple-50 dark:bg-[#5B5353]/30",
    button: "text-purple-600 hover:text-purple-700 dark:text-[#FFF1F3] dark:hover:text-[#FFF8F9]",
  },
} as const;

export function FileUploadDropzone({
  accept,
  dragActive,
  emptyStateLabel,
  helperText,
  inputId,
  isUploading,
  multiple = false,
  onChange,
  onDrag,
  onDrop,
  onReset,
  resetLabel,
  selectedFile,
  selectedFiles = [],
  selectedMode = "single",
  tone = "blue",
}: FileUploadDropzoneProps) {
  const styles = toneClasses[tone];
  const hasMultiSelection = selectedFiles.length > 0;
  const hasSelection = Boolean(selectedFile) || hasMultiSelection;

  return (
    <div
      onDragEnter={onDrag}
      onDragLeave={onDrag}
      onDragOver={onDrag}
      onDrop={onDrop}
      className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
        dragActive
          ? styles.active
          : "border-gray-300 dark:border-[#5B5353] hover:border-gray-400 dark:hover:border-[#948A8B]"
      }`}
    >
      {hasSelection ? (
        <div className="space-y-2">
          <svg
            className="mx-auto h-12 w-12 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>

          {selectedMode === "count-list" && hasMultiSelection ? (
            <>
              <div className="text-sm font-medium text-gray-900 dark:text-[#FFF8F9]">
                {selectedFiles.length} file{selectedFiles.length !== 1 ? "s" : ""} selected
              </div>
              <div className="text-xs text-gray-500 dark:text-[#B5A9AA] space-y-1">
                {selectedFiles.map((file, index) => (
                  <div key={`${file.name}-${index}`}>
                    {file.name} ({(file.size / 1024).toFixed(2)} KB)
                  </div>
                ))}
              </div>
            </>
          ) : selectedMode === "list" && hasMultiSelection ? (
            <div className="space-y-1">
              {selectedFiles.map((file, index) => (
                <div key={`${file.name}-${index}`}>
                  <div className="text-sm font-medium text-gray-900 dark:text-[#FFF8F9]">
                    {index + 1}. {file.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-[#B5A9AA]">
                    {(file.size / 1024).toFixed(2)} KB
                  </div>
                </div>
              ))}
            </div>
          ) : selectedFile ? (
            <>
              <div className="text-sm font-medium text-gray-900 dark:text-[#FFF8F9]">
                {selectedFile.name}
              </div>
              <div className="text-xs text-gray-500 dark:text-[#B5A9AA]">
                {(selectedFile.size / 1024).toFixed(2)} KB
              </div>
            </>
          ) : null}

          <button
            type="button"
            onClick={onReset}
            className={`text-sm ${styles.button}`}
          >
            {resetLabel}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="text-sm text-gray-600 dark:text-[#B5A9AA]">
            <label htmlFor={inputId} className="cursor-pointer">
              {emptyStateLabel}
            </label>
            <input
              id={inputId}
              name={inputId}
              type="file"
              accept={accept}
              className="sr-only"
              onChange={onChange}
              disabled={isUploading}
              multiple={multiple}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-[#B5A9AA]">
            {helperText}
          </p>
        </div>
      )}
    </div>
  );
}
